import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { GroupsService } from '@core/api/generated/api/groups.service';

/**
 * Guards the `groups/:groupId` route: the group must exist in the
 * currently selected project/branch. If it does not exist (e.g. because the
 * user switched to a branch where the group was not yet created, or the
 * group ID is simply invalid), the guard redirects to the Concepts
 * overview (`concepts`) instead of letting the detail page render an error.
 *
 * `projectId` and `branchName` are parameters of the parent route
 * (`projects/:projectId/:branchName`). Because `paramsInheritanceStrategy`
 * is set to `'always'` (see app.config.ts), `route.paramMap` already
 * contains the inherited parent params, so no manual `route.parent`
 * traversal is necessary.
 *
 * This guard should be applied as a `canActivate` guard on the
 * `groups/:groupId` route, complementing `projectExistsGuard` and
 * `branchExistsGuard`, which already validated `projectId`/`branchName`.
 */
export const groupExistsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const groupsService = inject(GroupsService);

  const projectId = route.paramMap.get('projectId');
  const branchName = route.queryParamMap.get('branchName');
  const groupId = route.paramMap.get('groupId');

  // If any required param is missing, let other guards/logic handle it
  // (should practically not happen, since parent guards already ensure
  // projectId/branchName, and the route itself declares :groupId).
  if (!projectId || !branchName || !groupId) {
    return true;
  }

  return groupsService.getGroup(projectId, branchName, groupId).pipe(
    map(() => true),
    catchError(() =>
      of(
        router.createUrlTree(['/projects', projectId, 'concepts'], { queryParams: { branchName } })
      )
    )
  );
};
